/**
 * Custom Hooks for Crew Changes
 * Provides React hooks for managing crew sign on/off tracking
 */

import { useCallback } from 'react';
import { useResourceCollection, useResourceById, useCreateResource, useUpdateResource, useDeleteResource, ResourceState, MutationState } from './useResource';
import { crewChangesService } from '../services';
import { CrewChange } from '../types';
import { ResourceQueryOptions } from '../services/resources.service';

/**
 * Hook to fetch all crew changes
 */
export function useCrewChanges(
  options?: ResourceQueryOptions
): ResourceState<CrewChange> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      crewChangesService.getAll(opts) as unknown as Promise<{ data: CrewChange[]; meta?: any }>,
    []
  );
  return useResourceCollection<CrewChange>(fetchFn, options);
}

/**
 * Hook to fetch a single crew change by ID
 */
export function useCrewChange(id: number | null, options?: ResourceQueryOptions) {
  return useResourceById<CrewChange>((id) => crewChangesService.getById(id, options), id);
}

/**
 * Hook to create a new crew change
 */
export function useCreateCrewChange(onSuccess?: () => void): MutationState<CrewChange> {
  return useCreateResource<CrewChange>(crewChangesService.create, onSuccess);
}

/**
 * Hook to update an existing crew change
 */
export function useUpdateCrewChange(onSuccess?: () => void): MutationState<CrewChange> {
  return useUpdateResource<CrewChange>(crewChangesService.update, onSuccess);
}

/**
 * Hook to delete a crew change
 */
export function useDeleteCrewChange(onSuccess?: () => void): MutationState<void> {
  return useDeleteResource(crewChangesService.delete, onSuccess);
}

